import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PublicShell } from "@/components/site/PublicShell";

const DELETED = [
  "Account profile (name, email, password hash, display preferences)",
  "Simulated (paper-trading) portfolio, holdings and trade history",
  "Watchlist, price / bracket / support-resistance alerts and notifications",
  "Decision journal entries and AI chat history",
  "Learn Crypto progress and quiz results",
  "Phone push-notification device registrations and delivery log",
  "Daily feature-usage counters and coin-search quota records",
  "Subscription and payment records held by EV-IDEX — any active Pro / Premium plan is cancelled immediately",
  "Login-protection records for your email address",
];

const KEPT = [
  "Feedback you submitted — kept in anonymised form only (your name, email and account link are removed at deletion) for up to 24 months after it is resolved",
  "EV-IDEX's own public prediction ledger (system-level market predictions, never linked to any user)",
  "Aggregate, anonymous service statistics that never identified you",
  "Server request logs (IP address, timestamp, requested URL) kept by our hosting platform for security; not linked to your account and expire automatically",
  "Invoices and receipts held by our payment processor (Stripe) for the legally required accounting period (typically 7 years)",
  "An anonymous record that a deletion happened (date and a random internal id only — no name, email or content)",
];

export default function DeleteAccount() {
  const { user } = useAuth();
  return (
    <PublicShell title="Delete your EV-IDEX account" subtitle="You can delete your account and all associated data yourself, at any time, directly inside the app — no email or support request needed. This applies to the EV-IDEX website, Android app and iOS app.">
      <div className="space-y-6" data-testid="delete-account-page">
        <section className="apx-card p-5 space-y-3" data-testid="delete-steps">
          <h2 className="font-display font-semibold text-lg">How to delete your account</h2>
          <ol className="list-decimal list-inside space-y-2 text-sm text-slate-300">
            <li>Sign in to EV-IDEX (website or mobile app).</li>
            <li>Open <strong>Settings</strong> (gear icon, or <em>More → Settings</em> on mobile).</li>
            <li>Scroll to the <strong className="text-rose-300">Danger zone</strong> card and tap <strong>Delete my account</strong>.</li>
            <li>Enter your password and type <code className="font-mono bg-slate-800 px-1 rounded">DELETE</code> to confirm.</li>
          </ol>
          <p className="text-xs text-slate-500">Deletion is immediate and permanent. You are signed out on all devices and the email address becomes available for a fresh sign-up.</p>
          {user ? (
            <Link to="/settings" data-testid="go-to-settings-btn" className="btn-primary inline-block rounded-lg px-4 py-2 text-sm font-medium">Go to Settings</Link>
          ) : (
            <Link to="/login?next=%2Fsettings" data-testid="login-to-delete-btn" className="btn-primary inline-block rounded-lg px-4 py-2 text-sm font-medium">Sign in to continue</Link>
          )}
        </section>

        <section className="grid sm:grid-cols-2 gap-4">
          <div className="apx-card p-5" data-testid="deleted-data-list">
            <h2 className="font-display font-semibold mb-2 text-rose-300">Deleted immediately</h2>
            <ul className="space-y-1.5 text-sm text-slate-300 list-disc list-inside">{DELETED.map((d) => <li key={d}>{d}</li>)}</ul>
          </div>
          <div className="apx-card p-5" data-testid="kept-data-list">
            <h2 className="font-display font-semibold mb-2 text-slate-200">Not affected</h2>
            <ul className="space-y-1.5 text-sm text-slate-400 list-disc list-inside">{KEPT.map((d) => <li key={d}>{d}</li>)}</ul>
            <p className="text-xs text-slate-500 mt-3">Nothing in this list can identify you after deletion. If you also want your anonymised feedback text removed, say so when you contact support (see below).</p>
          </div>
        </section>

        <p className="text-xs text-slate-500" data-testid="delete-help">
          Read our <Link to="/privacy" className="text-emerald-400 hover:underline" data-testid="delete-privacy-link">Privacy Policy</Link> for full details on the data we hold.{" "}
          Lost access to your account? Use the same email address you registered with and contact us from the app store listing's support link; we will verify ownership and delete the account for you within 30 days.
        </p>
      </div>
    </PublicShell>
  );
}
